import { Controller } from "@hotwired/stimulus"

// The ticked ones on the page, by suggestion id: kept when a figure's list is
// redrawn (a review moved out of it or into it), so the ticks stay.
const ticked = new Set()

// A figure's new reviews (dashboard/_figure_rows): how many are ticked to add,
// counted as they're ticked, beside the figure's picture that stays in view;
// each button named for what it does ("Add 3", "Move 2 to Iron Fist"), and a
// line for what Add does and how many reviews that leaves on the figure
// (has: those on it now), against the 3–6 it should have.
export default class extends Controller {
  static targets = ["count", "line", "add", "keep", "move"]
  static values = { has: Number }

  // Only what was ticked here: a browser putting back the boxes' ticks on a
  // reload goes by their place on the page, which is another figure's
  // reviews once the ones settled have left it.
  connect() {
    this.boxes.forEach((box) => { box.checked = ticked.has(box.value) })
    this.update()
  }

  update() {
    this.boxes.forEach((box) => box.checked ? ticked.add(box.value) : ticked.delete(box.value))
    const n = this.boxes.filter((box) => box.checked).length
    this.countTarget.textContent = `${n} ticked`
    this.countTarget.classList.toggle("on", n > 0)
    // Add and Move take the ticked ones: with none ticked there's nothing to add (Dismiss all is its own button).
    this.element.querySelectorAll("button[value='add'], button[value='keep'], button[value='move']").forEach((b) => { b.disabled = n === 0 })
    const rest = this.boxes.length - n
    if (this.hasAddTarget) this.addTarget.textContent = n ? `Add ${n}` : "Add"
    if (this.hasKeepTarget) this.keepTarget.textContent = n && rest ? `Add ${n}, keep ${rest}` : "Add, keep rest"
    if (this.hasMoveTarget) this.moveTarget.textContent = n ? `Move ${n}${this.moveTo ? ` to ${this.moveTo}` : ""}` : "Move ticked"
    if (this.hasLineTarget) this.lineTarget.textContent = this.said(n, rest)
  }

  // "Adds 3 of 6, dismisses 3 · it'll have 5 reviews (3–6 is the aim)".
  said(n, rest) {
    if (!n) return "Tick the ones to add: Add dismisses the rest."
    const after = this.hasValue + n
    const aim = after < 3 ? `${3 - after} short of the 3–6 it should have` : after > 6 ? `more than the 6 it should have at most` : "within the 3–6 it should have"
    return `Adds ${n} of ${this.boxes.length}${rest ? `, dismisses ${rest}` : ""} · it'll have ${after} ${after === 1 ? "review" : "reviews"}, ${aim}`
  }

  // The figure Move goes to, as picked: from the list, or found by search.
  get moveTo() {
    const select = this.element.querySelector("select[name='move_to']")
    const short = (name) => (name.length > 40 ? `${name.slice(0, 39)}…` : name)
    if (select?.value) return short(select.selectedOptions[0].text)
    const picked = this.element.querySelector("input[name='move_figure_id']")
    return picked?.value ? short(this.element.querySelector("input[name='move_search']")?.value || "") : null
  }

  // Enter in Move's figure boxes moves the ticked ones, rather than the
  // form's first button (Add) taking it.
  move(event) {
    event.preventDefault()
    this.element.querySelector("button[value='move']")?.click()
  }

  get boxes() {
    return [...this.element.querySelectorAll("input[name='add_ids[]']")]
  }
}
